import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { setTokens } from '../lib/api-client';
import { Spinner } from '../components/ui';

/**
 * Landing page after Google OAuth callback.
 * Reads access/refresh tokens from URL hash, stores them, loads user profile,
 * then navigates to the dashboard.
 */
export default function AuthRedirect() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    async function run() {
      try {
        // Tokens arrive in URL hash to keep them out of server/proxy logs
        const hash = window.location.hash.startsWith('#')
          ? window.location.hash.slice(1)
          : window.location.hash;
        const params = new URLSearchParams(hash);
        const access = params.get('access');
        const refresh = params.get('refresh');

        if (!access || !refresh) {
          setError('Tokens mancanti nell\'URL di callback.');
          return;
        }

        setTokens(access, refresh);

        // Clean the URL to remove tokens from browser history
        window.history.replaceState(null, '', '/auth-redirect');

        await refreshUser();
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        console.error('[AuthRedirect] error:', err);
        setError(err?.message || 'Errore durante l\'autenticazione.');
      }
    }

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-text-primary font-medium mb-4">{error}</p>
          <a
            href="/login"
            className="inline-block px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Torna al login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <Spinner size="lg" className="mx-auto mb-4" />
        <p className="text-text-secondary text-sm">Accesso in corso...</p>
      </div>
    </div>
  );
}
